//#region Util Imports

import * as _ from 'underscore'
import * as $ from 'jquery'

//#endregion

import { ColumnModel } from '../../modules/api/api.models'

import { CommonHelper, ToastHelper, LocalizationHelper, ConvertHelper, LogHelper } from '../../helpers'

import { OpOperator, OpDataTypeInGroup } from '../../models/core'
import { Enums } from '../../models/common'
import { IReferenceType, IOpObject } from '../../models/util/interfaces'

import { BulkEditInitParams, CreateEditInitParams } from '../../modules/common/create-edit-popup/create-edit-popup.controller'

import { GridService, GRID_SERVICE_NAME } from '../grid'
import { USER_SESSION_SERVICE_NAME, UserSessionService } from '../user-session'
import { SystemService, SYSTEM_SERVICE_NAME, DetailsOptions } from '../system'
import { DataTypeGroupService, DATA_TYPE_GROUP_SERVICE_NAME } from '../data-type'

//#region Consts

export const OPERATOR_SERVICE_NAME: string = 'OperatorService'
export const OPERATOR_SERVICE_PROVIDER_NAME: string = 'OperatorServiceProvider'

//#endregion

export class OperatorService {

    //#region Consts

    public static get LOG_PREFIX(): string { return '[SIMAX.Module.Operator]' }
    public static get FILE_NAME_PREFIX(): string { return 'operators' }
    public static get REFERENCE_TYPE(): Enums.ReferenceType { return Enums.ReferenceType.IdOperator }
    public static get SIMAX_MODULE_NAME(): Enums.SimaxModuleName { return Enums.SimaxModuleName.Operator }

    //#endregion


    //#region Ctor

    constructor(
        private dataTypeGroupService: DataTypeGroupService,
        private gridService: GridService,
        private systemService: SystemService,
        private userSessionService: UserSessionService
    ) { }

    //#endregion


    //#region Columns

    public getMainGridColumns(): JQueryPromise<ColumnModel[]> {
        return this.gridService.getColumnModelByCategory([
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__GRID_PARAMS,
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__DEFAULT_GRID_PARAMS
        ], OperatorService.SIMAX_MODULE_NAME)
    }

    //#endregion


    //#region Delete

    public deleteOperator(operatorToDelete: OpOperator): JQueryPromise<any> {
        const toDelete: OpOperator = operatorToDelete.clone()

        toDelete.idOperator = operatorToDelete.idOperator
        toDelete.opState = Enums.OpChangeState.Delete

        this.systemService.showLoadIndicator()

        return this
            .userSessionService
            .getDataProvider()
            .Operator
            .deleteOperator(toDelete)
            .then(() => {
                ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETED, OperatorService.SIMAX_MODULE_NAME))
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__DELETE_ERROR, OperatorService.SIMAX_MODULE_NAME))
                LogHelper.warn(OperatorService.LOG_PREFIX, `Unable to delete operator with id ${operatorToDelete.idOperator}.`, error)
            })
            .always(() => {
                this.systemService.hideLoadIndicator()
            })
    }

    //#endregion


    //#region Details

    public getDetailsOptions(idOperator: number, pageItemsIds: number[]): DetailsOptions {
        return {
            moduleName: OperatorService.SIMAX_MODULE_NAME,
            pageItemsIds: pageItemsIds,
            referenceObjectId: idOperator,
        }
    }

    //#endregion


    //#region Get

    public getOperator(idOperator): JQueryPromise<OpOperator> {
        return this.userSessionService
            .getDataProvider().Operator
            .getOperator(idOperator)
            .then((operator: OpOperator) => {
                if (operator != null) {
                    return operator;
                }

                return $.Deferred().reject(null).promise()
            })
            .fail((error: any) => {
                ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
                LogHelper.warn(OperatorService.LOG_PREFIX, `Unable to load operator for id ${idOperator}`, error)

                return null
            })
    }

    //#endregion


    //#region Parameters

    public getBasicOperatorParametersDataTypeGroup(): JQueryPromise<OpDataTypeInGroup[]> {
        return this.dataTypeGroupService.getDataTypeInGroupByCategory(
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
            OperatorService.SIMAX_MODULE_NAME
        )
    }

    public getBasicActorParametersDataTypeGroup(): JQueryPromise<OpDataTypeInGroup[]> {
        // TO DO From actor.service
        return this.dataTypeGroupService.getDataTypeInGroupByCategory(
            Enums.SimaxDataTypeGroupCategory.WEBSIMAX__MODULE_NAME__PANEL_PARAMS_TO_DISPLAY,
            Enums.SimaxModuleName.Actor
        )
    }

    //#endregion


    //#region Options

    public getCreateEditOptions(referenceObjectId: number = 0, referenceObject: IReferenceType & IOpObject = null, copy: boolean = false): CreateEditInitParams {
        const options: CreateEditInitParams = {
            createEditSelectedTabType: Enums.SimaxCreateEditSelectedTabType.BaseParameters,
            groupReferenceType: null,
            groupReferenceValue: null,
            idReferenceType: OperatorService.REFERENCE_TYPE,
            moduleName: OperatorService.SIMAX_MODULE_NAME,
            sections: null,
            useBusinessObjects: true,

            referenceObject,
            referenceObjectId,
        }

        options.isCopy = copy

        return options
    }

    public getBulkCreateEditOptions(gridData: any[], columns: any[]): BulkEditInitParams {
        return {
            gridColumns: columns,
            groupReferenceType: null,
            groupReferenceValue: null,
            idReferenceType: OperatorService.REFERENCE_TYPE,
            moduleName: OperatorService.SIMAX_MODULE_NAME,
            section: null,
            selectedGridRows: gridData,
            useBusinessObjects: true,
        }
    }

    //#endregion


    //#region Save

    public saveOperator(operator: OpOperator, isBulkSave: boolean = false): JQueryPromise<OpOperator> {
        return this.userSessionService
            .getDataProvider().Operator
            .saveOperator(operator)
            .then((savedObject: OpOperator) => {
                if (!isBulkSave) {
                    ToastHelper.showSuccess(LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVED, OperatorService.SIMAX_MODULE_NAME))
                    CommonHelper.setValueToLocalStorage(CommonHelper.localStorageVarForSavedEntity.userSaved, true, 0)
                }

                return savedObject
            })
            .fail((error: any) => {
                if (!isBulkSave) {
                    ToastHelper.showError(ConvertHelper.getRequestErrorMessage(error) || LocalizationHelper.getLocalizedTextByModule(Enums.SimaxLocalizationCategory._MODULE_NAME__SAVE_ERROR, OperatorService.SIMAX_MODULE_NAME))
                    LogHelper.warn(OperatorService.LOG_PREFIX, `Unable to save operator with id ${operator.idOperator}.`, error)
                }

                return $.Deferred().reject(error).promise()
            })
    }

    //#endregion
}

export class OperatorServiceProvider implements ng.IServiceProvider {

    //#region IServiceProvider Implementation 

    public $get = [
        DATA_TYPE_GROUP_SERVICE_NAME,
        GRID_SERVICE_NAME,
        SYSTEM_SERVICE_NAME,
        USER_SESSION_SERVICE_NAME,
        function (dataTypeGroupService: DataTypeGroupService, gridService: GridService, systemService: SystemService, userSession: UserSessionService) {
            return new OperatorService(dataTypeGroupService, gridService, systemService, userSession)
        }]

    //#endregion
} 